import { AddCircleOutline } from '@material-ui/icons';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { getDatetimePickerInputId } from '@monorepo/shared/componentsV2/dateAndTimePickers/DatetimePicker';
import { ErrorIcon } from '@monorepo/shared/componentsV2/errorDisplays/ErrorIcon';
import {
  LOG_DATE_FIELD_NAME,
  LogEntry,
} from '@monorepo/shared/componentsV2/logs/types';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { useToast } from '@monorepo/shared/contexts';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import AddIcon from '@svg/add.svg';
import TrashIcon from '@svg/trash.svg';
import ChevronLeftIcon from '@svg/chevron-left.svg';
import ChevronRightIcon from '@svg/chevron-right.svg';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

const SquareIconButton = styled(IconButton)`
  width: 36px;
  min-width: 36px;
  height: 36px;
  min-height: 36px;
  padding: 0;
  border: none;

  & svg {
    font-size: 20px;
  }

  &:disabled {
    border: none;
  }
`;

const GrayIconButton = styled(SquareIconButton)`
  background-color: ${({ theme }) => theme.colors.grayeee};

  &:hover {
    background-color: ${({ theme }) => theme.colors.grayddd};
  }

  &:disabled svg {
    color: ${({ theme }) => theme.colors.graybbb};
  }
`;

const NavigationButtonsContainer = styled.div`
  display: flex;
  gap: 20px;
`;

const SmallCaretLeft = styled(ChevronLeftIcon)`
  width: 16px;
  height: 16px;
  transform: scale(0.6);
`;

const SmallCaretRight = styled(ChevronRightIcon)`
  width: 16px;
  height: 16px;
  transform: scale(0.6);
`;

const ErrorIconDivContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

export function useModalActionLeftButtons({
  logEntries,
  setLogEntries,
  logEntryIDsToDelete = [],
  setLogEntryIDsToDelete,
  isReadOnly,
  deleteConfirmationDescription,
  entryFormPageNumber,
  reviewPageNumber,
  initialLogEntryId,
  edpAiSubmissionId,
}: {
  logEntries: LogEntry[] | undefined;
  setLogEntries: (logEntries: LogEntry[]) => void;
  logEntryIDsToDelete: string[];
  setLogEntryIDsToDelete: (logEntryIDs: string[]) => void;
  isReadOnly: boolean;
  deleteConfirmationDescription: string;
  entryFormPageNumber: number;
  reviewPageNumber?: number;
  initialLogEntryId?: string;
  edpAiSubmissionId?: string;
}) {
  const hasSetInitialLogEntryIndex = useRef(false);
  const pendingFocusEntryId = useRef<string | null>(null);
  const { showUserFriendlyErrorToast } = useToast();
  const [logEntryIndex, setLogEntryIndex] = useState(0);
  const canModifyEntries = !!logEntries?.length && !isReadOnly;

  const handleNextLogEntry = useCallback(() => {
    if (!logEntries || logEntryIndex === logEntries.length - 1) return;
    setLogEntryIndex(Math.min(logEntryIndex + 1, logEntries.length - 1));
  }, [logEntryIndex, logEntries, setLogEntryIndex]);

  const handlePreviousLogEntry = useCallback(() => {
    if (!logEntries || logEntryIndex === 0) return;
    setLogEntryIndex(Math.max(logEntryIndex - 1, 0));
  }, [logEntryIndex, logEntries, setLogEntryIndex]);

  const { confirm } = useModal();
  const onDeleteLogEntryWithConfirmation = useCallback(async () => {
    if (!logEntries?.length) return;

    const confirmed = await confirm({
      title: 'Are you sure you want to delete?',
      description: deleteConfirmationDescription,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Delete 1 entry',
      danger: true,
    });

    if (confirmed) {
      const updatedLogEntries = logEntries.filter(
        (_, index) => index !== logEntryIndex,
      );
      setLogEntries(updatedLogEntries);
      setLogEntryIndex(
        // Go back by 1 if we're on the rightmost entry, otherwise keep the same index (which will now point to the
        // entry that was right of the deleted one).
        logEntryIndex === logEntries.length - 1
          ? logEntryIndex - 1
          : logEntryIndex,
      );
    }
  }, [
    deleteConfirmationDescription,
    logEntryIndex,
    logEntries,
    confirm,
    setLogEntries,
  ]);

  const createNewLogEntry = useCallback((): LogEntry | undefined => {
    // grab the last log entry and use its logFieldValues as the basis for the new log entry
    // set the value: null for all fields
    const lastLogEntry = logEntries?.[logEntries.length - 1];
    if (!lastLogEntry) {
      showUserFriendlyErrorToast(
        new Error('Cannot create new log entry without an existing entry'),
      );
      return undefined;
    }
    const { logFieldValues } = lastLogEntry;
    const emptyLogFieldValues = Object.entries(logFieldValues).reduce<
      typeof logFieldValues
    >((acc, [fieldId, fieldValueResponse]) => {
      acc[fieldId] = {
        ...fieldValueResponse,
        fieldValue: {
          ...fieldValueResponse.fieldValue,
          value: null,
        },
      };
      return acc;
    }, {});
    return {
      ...lastLogEntry,
      // A new ID is needed to identify the new log entry to make updates to the state
      // This ID does not persist to the backend/db and will get a new ID when the entry is saved
      id: uuidv4(),
      logFieldValues: emptyLogFieldValues,
      reviewedBy: null,
      edpAiSubmissionId: edpAiSubmissionId ?? lastLogEntry.edpAiSubmissionId,
    };
  }, [edpAiSubmissionId, logEntries, showUserFriendlyErrorToast]);

  const handleAddLogEntry = useCallback(() => {
    const newLogEntry = createNewLogEntry();
    if (!newLogEntry) return;

    const nextLogEntries = [...(logEntries ?? []), newLogEntry];
    setLogEntries(nextLogEntries);
    setLogEntryIndex(nextLogEntries.length - 1);
    pendingFocusEntryId.current = newLogEntry.id;
  }, [createNewLogEntry, logEntries, setLogEntries]);

  useLayoutEffect(() => {
    const entryId = pendingFocusEntryId.current;
    if (!entryId) return;

    const dateInput = document.querySelector<HTMLElement>(
      `[id^="${getDatetimePickerInputId(
        `${entryId}.${LOG_DATE_FIELD_NAME}`,
      )}"]`,
    );
    if (!dateInput) return;

    pendingFocusEntryId.current = null;
    dateInput.focus({ preventScroll: true });
  }, [logEntries, logEntryIndex]);

  const renderModalActionLeftButtons = useCallback(
    (pageNumber: number) => {
      const allEntriesDisabledText =
        'You cannot remove all entries from a submission';

      if (pageNumber === entryFormPageNumber) {
        return (
          <NavigationButtonsContainer>
            <Tooltip placement="top" title="Previous Entry">
              <GrayIconButton
                onClick={handlePreviousLogEntry}
                disabled={logEntryIndex === 0}
                variant="outlined"
                size="small"
              >
                <VisuallyHidden>Previous Entry</VisuallyHidden>
                <SmallCaretLeft />
              </GrayIconButton>
            </Tooltip>
            <Tooltip placement="top" title="Next Entry">
              <GrayIconButton
                onClick={handleNextLogEntry}
                disabled={!logEntries || logEntryIndex >= logEntries.length - 1}
                variant="outlined"
                size="small"
              >
                <VisuallyHidden>Next Entry</VisuallyHidden>
                <SmallCaretRight />
              </GrayIconButton>
            </Tooltip>
            {canModifyEntries && (
              <>
                <Tooltip placement="top" title="Add entry">
                  <GrayIconButton
                    variant="outlined"
                    onClick={handleAddLogEntry}
                  >
                    <VisuallyHidden>Add another entry</VisuallyHidden>
                    <AddIcon aria-hidden />
                  </GrayIconButton>
                </Tooltip>
                {logEntries && logEntries.length > 1 && (
                  <Tooltip
                    placement="top"
                    title={
                      logEntries.length <= 1
                        ? allEntriesDisabledText
                        : 'Delete entry'
                    }
                  >
                    <SquareIconButton
                      color="secondary"
                      variant="contained"
                      onClick={onDeleteLogEntryWithConfirmation}
                    >
                      <VisuallyHidden>Delete current entry</VisuallyHidden>
                      <TrashIcon aria-hidden />
                    </SquareIconButton>
                  </Tooltip>
                )}
              </>
            )}
          </NavigationButtonsContainer>
        );
      }

      if (pageNumber === reviewPageNumber && canModifyEntries) {
        let deleteButtonLabel = `Delete ${logEntryIDsToDelete.length} entries`;
        if (logEntryIDsToDelete.length === 1) {
          deleteButtonLabel = 'Delete 1 entry';
        }

        return (
          <div>
            <Button
              color="primary"
              variant="outlined"
              onClick={handleAddLogEntry}
              style={{ marginRight: '1rem' }}
            >
              <AddCircleOutline aria-hidden /> Add another entry
            </Button>
            {logEntryIDsToDelete.length > 0 && (
              <Button
                color="secondary"
                variant="contained"
                focusableWhenDisabled
                onClick={async () => {
                  if (logEntryIDsToDelete.length === logEntries.length) {
                    await confirm({
                      title: (
                        <ErrorIconDivContainer>
                          <ErrorIcon /> One entry required
                        </ErrorIconDivContainer>
                      ),
                      description:
                        'All submissions must have at least one entry.',
                      cancelButtonText: 'Cancel',
                      confirmButtonText: 'Okay',
                      showCloseButton: true,
                      danger: true,
                    });
                    return;
                  }

                  setLogEntries(
                    logEntries.filter(
                      (entry) => !logEntryIDsToDelete.includes(entry.id),
                    ),
                  );
                  setLogEntryIDsToDelete([]);
                  setLogEntryIndex(0);
                }}
              >
                {deleteButtonLabel}
              </Button>
            )}
          </div>
        );
      }

      return <div />;
    },
    [
      logEntryIDsToDelete,
      logEntries,
      entryFormPageNumber,
      reviewPageNumber,
      canModifyEntries,
      handlePreviousLogEntry,
      logEntryIndex,
      handleNextLogEntry,
      handleAddLogEntry,
      onDeleteLogEntryWithConfirmation,
      confirm,
      setLogEntries,
      setLogEntryIDsToDelete,
    ],
  );

  useEffect(() => {
    if (
      !hasSetInitialLogEntryIndex.current &&
      logEntries &&
      initialLogEntryId
    ) {
      const initialIndex = logEntries.findIndex(
        (logEntry) => logEntry.id === initialLogEntryId,
      );
      setLogEntryIndex(initialIndex === -1 ? 0 : initialIndex);
      hasSetInitialLogEntryIndex.current = true;
    }
  }, [initialLogEntryId, logEntries]);
  return {
    renderModalActionLeftButtons,
    logEntryIndex,
    setLogEntryIndex,
  };
}
